import { useCallback, useEffect, useState } from "react";
import { createPortalClient } from "@/services/portalClient";
import { isPushSupported, urlBase64ToUint8Array } from "./pushSupport";

/**
 * Subscribe-on-load self-healing (TD-003): every portal visit with
 * granted permission re-upserts the subscription, so a dead channel is
 * repaired by simply opening the portal.
 */
export function usePushSetup(clientId: string) {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  // True only after the subscription has been POSTed to the backend this
  // session — "permission granted" alone must never be presented as
  // "notifications active" (an empty VAPID key or a failed POST would lie).
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = useCallback(async () => {
    if (!isPushSupported() || !clientId) return;
    try {
      const registration = await navigator.serviceWorker.register(
        "/portal-sw.js"
      );
      await navigator.serviceWorker.ready;

      const portalClient = createPortalClient(clientId, "client");
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const { publicKey } = await portalClient.get<{ publicKey: string }>(
          "/portal/push/vapid-public-key"
        );
        if (!publicKey) {
          console.error(
            "[push] backend returned an empty VAPID public key — " +
              "VAPID_PUBLIC_KEY is not configured on the server; " +
              "subscription impossible"
          );
          return;
        }
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
      await portalClient.post(`/portal/push/${clientId}/subscribe`, {
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      });
      setSubscribed(true);
    } catch (err) {
      console.error("[push] subscribe failed:", err);
    }
  }, [clientId]);

  useEffect(() => {
    if (permission === "granted") {
      void subscribe();
    }
  }, [permission, subscribe]);

  const enable = useCallback(async (): Promise<NotificationPermission> => {
    if (!isPushSupported()) return "denied";
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  /**
   * Best-effort server-side unsubscribe for a user-initiated logout. Must
   * never block logout, so failures are swallowed. The browser-side
   * subscription is left intact; the next authenticated visit self-heals it.
   */
  const unsubscribe = useCallback(async () => {
    if (!isPushSupported() || !clientId) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration(
        "/portal-sw.js"
      );
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await createPortalClient(clientId, "client").post(
          `/portal/push/${clientId}/unsubscribe`,
          { endpoint: subscription.endpoint }
        );
      }
    } catch {
      // best-effort: logout must not be blocked by push cleanup
    }
  }, [clientId]);

  return { permission, subscribed, subscribe, enable, unsubscribe };
}
